/* eslint-disable react-refresh/only-export-components */

import React, { useContext, useMemo } from "react";
import type { ReactNode } from "react";
import { useU2000GridStateHandlers } from "../u2000Core/reducer/formReducer/U2000GridStateHandlers";
import { useU2000SearchStateHandlers } from "../u2000Core/reducer/formReducer/U2000SearchStateHandlers";
import type { U2000GridStateHandlersReturn } from "../u2000Core/reducer/formReducer/U2000GridStateHandlers";
import type { U2000SearchStateHandlersReturn } from "../u2000Core/reducer/formReducer/U2000SearchStateHandlers";
import { useU2000LUStateHandlers } from "../u2000Core/reducer/formReducer/U2000LUHandlers";
import type { U2000LUState } from "../u2000Core/reducer/formReducer/U2000LUState";
import type { U2102Role } from "./U2102Service";

// Type for initLU (from LUState.initLU)
export type InitLU = U2000LUState["personalization"] & { launchSearch: boolean; hideSearch: boolean; nbRecordPerPage: number };

export interface U2102ContextType {
  luStateHandlers: ReturnType<typeof useU2000LUStateHandlers>;
  searchStateHandlers: U2000SearchStateHandlersReturn;
  gridStateHandlers: U2000GridStateHandlersReturn<U2102Role>;
}

interface U2102ContextProviderProps {
  initLU: InitLU;
  children: ReactNode;
}

const U2102Context = React.createContext<U2102ContextType | undefined>(undefined);

export const U2102ContextProvider: React.FC<U2102ContextProviderProps> = ({ initLU, children }) => {
  const U2000LUStateHandlers = useU2000LUStateHandlers({ initLU: initLU as InitLU & U2000LUState });
  const U2000SearchStateHandlers = useU2000SearchStateHandlers({ luName: "u2102", launchSearch: initLU.launchSearch, hideSearch: initLU.hideSearch });
  const U2000GridStateHandlers = useU2000GridStateHandlers<U2102Role>({ nbRecordPerPage: initLU.nbRecordPerPage ?? 50 });

  const contextValue = useMemo<U2102ContextType>(
    () => ({
      luStateHandlers: U2000LUStateHandlers,
      searchStateHandlers: U2000SearchStateHandlers,
      gridStateHandlers: U2000GridStateHandlers,
    }),
    [U2000GridStateHandlers, U2000SearchStateHandlers, U2000LUStateHandlers]
  );

  return <U2102Context.Provider value={contextValue}>{children}</U2102Context.Provider>;
};

export function useU2102Context(): U2102ContextType {
  const context = useContext(U2102Context);
  if (!context) {
    throw new Error("useU2102Context must be used within a U2102ContextProvider");
  }
  return context;
}
